import { formatDayHeading } from '../../lib/formatDate';

/**
 * The feed, with its separators worked out.
 *
 * Returns a flat list of rows rather than nested groups, because that is what a feed
 * is: separators are entries in the stream, not containers around it. Keeping it flat
 * means the round rule and the date rule can disagree — which they do, since a round
 * can run across two days and a day can hold two rounds — without either having to
 * win.
 *
 * Posts arrive newest-first and stay that way. A separator is emitted above the first
 * post of its group, so reading down you meet the heading before the messages it
 * covers.
 *
 * Round separators are only emitted for posts that actually carry a `gameWeek`, which
 * today means the Gaffer's. Player posts are not stamped with the week they were
 * made, so inferring one for them would be inventing it — and a separator that says
 * ROUND 5 above a message from round 4 is worse than no separator. Stamping
 * `game_week` on player posts is a backend change; this will start using it the day
 * the field arrives, with no change here.
 */
export function buildFeed(posts) {
  const rows = [];
  let lastDay = null;
  let lastRound = null;

  posts.forEach((post) => {
    const day = dayKey(post.createdAt);
    if (day && day !== lastDay) {
      rows.push({ kind: 'day', key: `day-${day}`, label: formatDayHeading(post.createdAt) });
      lastDay = day;
    }

    const round = post.gameWeek;
    if (round != null && round !== lastRound) {
      rows.push({ kind: 'round', key: `round-${round}-${post.id}`, round });
      lastRound = round;
    }

    rows.push({ kind: 'post', key: `post-${post.id}`, post });
  });

  return rows;
}

function dayKey(dateTime) {
  if (!dateTime) return null;
  const d = new Date(dateTime);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
